import React, { useEffect, useState } from 'react';
import { detectLanguage } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getTranslation } from '../translations';

const PrivacyPolicy: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const isKorean = lang === 'ko';

  return (
    <div className="font-sans antialiased bg-gray-50">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      <div className="max-w-4xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {isKorean ? '개인정보 처리방침' : 'Privacy Policy'}
        </h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-6">
            {isKorean 
              ? '최종 업데이트: 2025년 8월 1일'
              : 'Last Updated: August 1, 2025'}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '1. 개요' : '1. Overview'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? 'Groove Inc.("회사", "우리")는 AivueBiz Desktop 서비스 사용자의 개인정보를 중요하게 생각합니다. 이 개인정보 처리방침은 우리가 수집하는 정보, 사용 방법 및 귀하의 권리에 대해 설명합니다.'
                : 'Groove Inc. ("Company", "we", "us") values the privacy of users of our AivueBiz Desktop service. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your information.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '2. 수집하는 정보' : '2. Information We Collect'}
            </h2>
            
            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '2.1 계정 정보' : '2.1 Account Information'}
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>{isKorean ? '이메일 주소' : 'Email address'}</li>
              <li>{isKorean ? '사용자 이름' : 'Username'}</li>
              <li>{isKorean ? '프로필 사진 (선택사항)' : 'Profile picture (optional)'}</li>
              <li>{isKorean ? '선호 언어 설정' : 'Language preferences'}</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '2.2 통신 데이터' : '2.2 Communication Data'}
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>{isKorean ? '채팅 메시지 (암호화됨)' : 'Chat messages (encrypted)'}</li>
              <li>{isKorean ? '통화 메타데이터 (시간, 기간)' : 'Call metadata (time, duration)'}</li>
              <li>{isKorean ? '공유 파일' : 'Shared files'}</li>
              <li>{isKorean ? 'STT 변환 텍스트 (임시 저장)' : 'STT transcription text (temporarily stored)'}</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '2.3 사용 데이터' : '2.3 Usage Data'}
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>{isKorean ? '기능 사용 통계' : 'Feature usage statistics'}</li>
              <li>{isKorean ? '앱 성능 데이터' : 'App performance data'}</li>
              <li>{isKorean ? '오류 로그' : 'Error logs'}</li>
              <li>{isKorean ? '디바이스 정보 (OS, 버전)' : 'Device information (OS, version)'}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '3. 정보 사용 방법' : '3. How We Use Information'}
            </h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? '서비스 제공 및 유지관리'
                  : 'To provide and maintain our Service'}
              </li>
              <li>
                {isKorean
                  ? '사용자 계정 관리 및 인증'
                  : 'To manage user accounts and authentication'}
              </li>
              <li>
                {isKorean
                  ? '실시간 번역 및 STT 서비스 제공'
                  : 'To provide real-time translation and STT services'}
              </li>
              <li>
                {isKorean
                  ? '서비스 개선 및 새로운 기능 개발'
                  : 'To improve our Service and develop new features'}
              </li>
              <li>
                {isKorean
                  ? '기술 지원 제공'
                  : 'To provide customer support'}
              </li>
              <li>
                {isKorean
                  ? '법적 의무 준수'
                  : 'To comply with legal obligations'}
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '4. 제3자 서비스' : '4. Third-Party Services'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 다음 제3자 서비스를 사용합니다:'
                : 'We use the following third-party services:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                <strong>Supabase:</strong> {isKorean ? '인증 및 데이터베이스' : 'Authentication and database'}
              </li>
              <li>
                <strong>ConnectyCube:</strong> {isKorean ? '실시간 통신' : 'Real-time communication'}
              </li>
              <li>
                <strong>Deepgram:</strong> {isKorean ? '음성 인식 (STT)' : 'Speech recognition (STT)'}
              </li>
              <li>
                <strong>DeepL/Google Translate:</strong> {isKorean ? '번역 서비스' : 'Translation services'}
              </li>
              <li>
                <strong>Cloudflare R2:</strong> {isKorean ? '파일 저장' : 'File storage'}
              </li>
              <li>
                <strong>LemonSqueezy:</strong> {isKorean ? '결제 처리' : 'Payment processing'}
              </li>
            </ul>
            <p className="text-gray-700 mt-4">
              {isKorean
                ? '각 서비스는 자체 개인정보 보호정책을 가지고 있으며, 우리는 필요한 최소한의 데이터만 공유합니다.'
                : 'Each service has its own privacy policy, and we share only the minimum necessary data.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '5. 데이터 보안' : '5. Data Security'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 다음과 같은 보안 조치를 구현합니다:'
                : 'We implement the following security measures:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>{isKorean ? '종단 간 암호화 (E2E) 통신' : 'End-to-end encrypted (E2E) communications'}</li>
              <li>{isKorean ? 'TLS/SSL 암호화 전송' : 'TLS/SSL encrypted transmission'}</li>
              <li>{isKorean ? '안전한 데이터 저장' : 'Secure data storage'}</li>
              <li>{isKorean ? '정기적인 보안 감사' : 'Regular security audits'}</li>
              <li>{isKorean ? '접근 제어 및 인증' : 'Access controls and authentication'}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '6. 데이터 보존' : '6. Data Retention'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '데이터 보존 기간은 구독 플랜에 따라 다릅니다:'
                : 'Data retention periods vary by subscription plan:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>{isKorean ? '무료: 1개월' : 'Free: 1 month'}</li>
              <li>{isKorean ? '개인: 3개월' : 'Personal: 3 months'}</li>
              <li>{isKorean ? '비즈니스: 6개월' : 'Business: 6 months'}</li>
              <li>{isKorean ? '엔터프라이즈: 맞춤형' : 'Enterprise: Custom'}</li>
            </ul>
            <p className="text-gray-700 mt-4">
              {isKorean
                ? '계정 삭제 시, 귀하의 데이터는 30일 이내에 영구적으로 삭제됩니다.'
                : 'Upon account deletion, your data will be permanently deleted within 30 days.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '7. 귀하의 권리' : '7. Your Rights'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '귀하는 다음과 같은 권리를 가집니다:'
                : 'You have the following rights:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                <strong>{isKorean ? '접근권:' : 'Access:'}</strong> {isKorean 
                  ? '귀하의 개인정보 사본 요청'
                  : 'Request a copy of your personal data'}
              </li>
              <li>
                <strong>{isKorean ? '수정권:' : 'Correction:'}</strong> {isKorean 
                  ? '부정확한 정보 수정 요청'
                  : 'Request correction of inaccurate information'}
              </li>
              <li>
                <strong>{isKorean ? '삭제권:' : 'Deletion:'}</strong> {isKorean 
                  ? '개인정보 삭제 요청'
                  : 'Request deletion of your personal data'}
              </li>
              <li>
                <strong>{isKorean ? '이동권:' : 'Portability:'}</strong> {isKorean 
                  ? '구조화된 형식으로 데이터 수령'
                  : 'Receive your data in a structured format'}
              </li>
              <li>
                <strong>{isKorean ? '거부권:' : 'Objection:'}</strong> {isKorean 
                  ? '특정 데이터 처리에 대한 거부'
                  : 'Object to certain data processing'}
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '8. 쿠키 정책' : '8. Cookie Policy'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 다음 목적으로 쿠키를 사용합니다:'
                : 'We use cookies for the following purposes:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>{isKorean ? '세션 관리' : 'Session management'}</li>
              <li>{isKorean ? '사용자 선호 설정 저장' : 'Storing user preferences'}</li>
              <li>{isKorean ? '분석 및 성능 개선' : 'Analytics and performance improvement'}</li>
            </ul>
            <p className="text-gray-700 mt-4">
              {isKorean
                ? '브라우저 설정에서 쿠키를 비활성화할 수 있지만, 일부 기능이 제한될 수 있습니다.'
                : 'You can disable cookies in your browser settings, but some features may be limited.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '9. 아동 개인정보' : '9. Children\'s Privacy'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리 서비스는 16세 미만의 아동을 대상으로 하지 않습니다. 16세 미만 아동의 개인정보를 의도적으로 수집하지 않습니다.'
                : 'Our Service is not directed to children under 16. We do not knowingly collect personal information from children under 16.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '10. 국제 데이터 전송' : '10. International Data Transfers'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '귀하의 데이터는 서비스 제공을 위해 다른 국가로 전송될 수 있습니다. 우리는 적절한 보호 조치를 통해 데이터를 안전하게 보호합니다.'
                : 'Your data may be transferred to other countries for service provision. We ensure appropriate safeguards are in place to protect your data.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '11. 개인정보 처리방침 변경' : '11. Changes to Privacy Policy'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 이 개인정보 처리방침을 수시로 업데이트할 수 있습니다. 중요한 변경 사항은 이메일로 통지됩니다.'
                : 'We may update this Privacy Policy from time to time. Significant changes will be notified via email.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '12. 연락처' : '12. Contact Information'}
            </h2>
            <p className="text-gray-700">
              {isKorean
                ? '개인정보 보호에 관한 질문이 있으시면 다음으로 연락하세요:'
                : 'For privacy-related questions, please contact us at:'}
            </p>
            <p className="text-gray-700 mt-4">
              <strong>{isKorean ? '개인정보 보호 담당자' : 'Privacy Officer'}</strong><br />
              Groove Inc.<br />
              Seoul, Republic of Korea<br />
              Email: privacy@aivuebiz.com<br />
              {isKorean 
                ? '일반 문의: support@aivuebiz.com'
                : 'General inquiries: support@aivuebiz.com'}
            </p>
          </section>
        </div>
      </div>
      
      <Footer t={t} />
    </div>
  );
};

export default PrivacyPolicy;